/**
 * The clans of the players in the game client (item 8 of the owner, the client side of docs/backend.md section 19), on
 * the screens of the original with minimal changes:
 *  - the arsenal's tab "Друзья" (wait_fd, the VK friends of the original) is the clans tab: the screen clan_hub on the
 *    symbol of the VK friends list (menu_friends; asset patches make its panel more opaque, put its list below a
 *    header and move its slider along). A player in a clan sees one short scrollable overview: the header (emblem,
 *    name, rating, members, territories, join policy, description), then "Игроки" (roles, levels, captures),
 *    "Территории" (a click opens the zone on the assault map) and "История" (the clan's events, page by page). Leader
 *    and officers select a player to kick, promote or demote him or hand the leadership over, decide the requests
 *    (the green button "Заявки (N)") and edit the clan (the leader everything, an officer the description). A player
 *    without a clan sees the clans by rating (or the search results by name), the own pending requests, "Создать
 *    клан" and why it is not possible yet;
 *  - the card of a clan (clan_card, a popup on the symbol of the original's clan member list, menu_clan_members, which
 *    nothing in the original could open) from the clan list, the best clans and the assault map: the header, the
 *    players, the territories and the history, and the button to join, to ask to join or to withdraw the request;
 *  - the dialog of a new clan and of the settings (clan_edit, the same popup): the name, the emblem (one of
 *    limits.clans.emblems: 7 drawings of the original and 8 of the game's flags, asset patches), open joining (the
 *    check box of the settings screen), the description; the leader disbands the clan there;
 *  - the confirmations (menu_question, modes clan_leave / clan_kick / clan_transfer / clan_disband: the clan's name
 *    typed again);
 *  - the best clans (menu_clans, the button of the assault map): the first page of clan.list by rating, its rows open
 *    the card;
 *  - the server's events (clan.updated, clan.membership, clan.requestsChanged) arrive in the game loop (a queue, never
 *    the blocking NET_INIT bridge) and update what is on screen.
 * The emblems next to the players of the tops and on the zones of the assault map come with their entries (FWP
 * clan_of_uid); the player's own clan (FWP.user_clan) with clan.mine (adoptMine).
 *
 * Browser-safe: no node imports; the protocol shapes are read structurally.
 */
import { Point, Shape, Sprite, TextField, TextFormat, type DisplayObject } from '@flash';
import { CLAN_DESCRIPTION_MAX, CLAN_EMBLEMS, CLAN_NAME_MAX, CLAN_NAME_MIN, checkClanDescription, checkClanName } from '../../shared/clan-rules';
import * as T from './clan-texts';
import { playerName, translate } from './i18n';
import { sessionEnded } from './net-policy';
import { ERROR_COLOR, HINT_COLOR, LABEL_COLOR, fitText, inputField, label, setRawText } from '../ui/controls';
import { listPitch } from '../ui/wide-lists';

// The protocol's clan shapes as the game reads them (ClanSummary, ClanInfo, ClanMember, ClanJoinRequest, ClanEvent and
// ClanMineResult of src/shared/protocol/model.ts; the game never imports the protocol).
type ClanRole = 'leader' | 'officer' | 'member';
interface ClanSummary {
  clanId: number;
  name: string;
  rating: number;
  memberCount: number;
  emblem: number;
  joinPolicy: 'open' | 'request';
  zoneCount: number;
  maxMembers: number;
}
interface ClanZone {
  zone: number;
  ownerId: string;
  ownerName: string;
  stage: number;
  lastCaptureAt: number;
  tributePerHour: number;
}
interface ClanInfo extends ClanSummary {
  description: string;
  leaderId: string;
  leaderName: string;
  createdAt: number;
  zones: ClanZone[];
}
interface ClanMember {
  playerId: string;
  displayName: string;
  level: number;
  captures: number;
  role: ClanRole;
  joinedAt: number;
}
interface ClanJoinRequest {
  clanId: number;
  clanName: string;
  playerId: string;
  displayName: string;
  level: number;
  at: number;
}
type ClanEvent = T.EventLike & { seq: number; at: number };
interface ClanMineResult {
  clan: ClanInfo | null;
  role: ClanRole | null;
  requests: ClanJoinRequest[];
  pendingRequests: number;
  rejoinAt: number | null;
  canCreate: { ok: boolean; reason?: string; at?: number };
}

/** Frame of clan_icons shown for "no clan" (an empty frame of the original). */
export const NO_EMBLEM_FRAME = 29;

/** The clan_icons frame of an emblem (NO_EMBLEM_FRAME for anything that is not a picture of the sprite). */
export function emblemFrame(emblem: unknown): number {
  const n = Number(emblem);
  return Number.isInteger(n) && n >= 1 && n <= 28 ? n : NO_EMBLEM_FRAME;
}

/** Shows a clan's emblem in a clan_icons clip (stopped on its frame). */
export function setEmblem(clip: any, emblem: unknown): void {
  if (clip && typeof clip.gotoAndStop === 'function') clip.gotoAndStop(emblemFrame(emblem));
}

/** The screens of this module. */
const SCREENS = ['clan_hub', 'clan_card', 'clan_edit'] as const;
type ClanScreen = (typeof SCREENS)[number];
/** The confirmation modes of menu_question. */
const QUESTIONS = ['clan_leave', 'clan_kick', 'clan_transfer', 'clan_disband'] as const;

/** Colours: the headings of the lists ("Режим игры" blue), the notes, the chosen row, the dates, the rating. */
const HEADING_COLOR = 0x0066ff;
const NOTE_COLOR = 0x666666;
const CHOSEN_COLOR = 0xff0000;
const DATE_COLOR = 0x777777;
const INFO_COLOR = 0x333333;
const RATING_COLOR = 0x009900;

/**
 * menu_friends as the clans screen (the asset patches clans-*): the unnamed title (its placement), the header, the
 * list (friends_container and its mask: 251..568 x 201..459, a gap above the line under it), the slider's range, the
 * two rows of actions below it.
 */
const HUB = {
  title: { at: { x: 359.45, y: 116 }, x: 240, width: 360 },
  meetCaption: { x: 440.65, y: 525.75 },
  meetShape: { x: 107, y: 59.55 },
  emblem: { x: 252, y: 137 },
  name: { x: 292, y: 134, width: 296 },
  policy: { x: 438, y: 137, width: 150 },
  facts: { x: 292, y: 151, width: 296 },
  desc: { x: 252, y: 169, width: 336 },
  intro: { x: 244, y: 136, width: 350 },
  list: { x: 251, top: 201, height: 258, sliderTop: 216, sliderBottom: 448 },
  selected: { x: 251, y: 464, width: 340 },
  f1Buttons: { y: 478, x: [251, 341, 431, 521] },
  create: { x: 251, y: 466 },
  createNote: { x: 402, y: 467, width: 190 },
  leave: { x: 251, y: 516 },
  search: { x: 265, y: 506, width: 160 },
  status: { x: 251, y: 558, width: 340 },
};
/** menu_clan_members as the card of a clan (the asset patches clan-card-*: the list 235..552 x 196..440). */
const CARD = {
  emblem: { x: 236, y: 104 },
  name: { x: 276, y: 101, width: 262 },
  facts: { x: 276, y: 119, width: 262 },
  desc: { x: 236, y: 150, width: 306 },
  list: { x: 235, top: 196, height: 244, sliderTop: 211, sliderBottom: 425 },
  action: { x: 330, y: 449 },
  status: { x: 270, y: 487, width: 270 },
};
/** menu_clan_members as the dialog of a new clan / the settings (the vine on the right from x 545 stays free). */
const EDIT = {
  title: { x: 215, y: 100, width: 375 },
  nameLabel: { x: 240, y: 128 },
  name: { x: 362, y: 125, width: 178 },
  emblemLabel: { x: 240, y: 154 },
  emblems: { x: 244, y: 174, dx: 38, dy: 40, perRow: 8 },
  open: { x: 240, y: 258 },
  openLabel: { x: 264, y: 258, width: 280 },
  descLabel: { x: 240, y: 284, width: 305 },
  desc: { x: 240, y: 302, width: 305, height: 46 },
  rules: { x: 240, y: 354, width: 305 },
  error: { x: 240, y: 408, width: 305 },
  save: { x: 330, y: 444 },
  saveSettings: { x: 405, y: 444 },
  disband: { x: 260, y: 444 },
};
/** menu_question in the clan modes: the field of the disband confirmation. */
const QUESTION_INPUT = { x: 303, y: 306, width: 200 };
/** Rows of the lists (choose_task: 20 px; a longer text wraps, 12 px more for each line after the first). */
const ROW = 20;
const ROW_LINE = 12;
const CLAN_ROW = 34;
const SECTION_GAP = 6;
/** Pages: the clans of the list, the history, the requests; the best clans (the 14 rows of menu_clans). */
const CLAN_PAGE = 20;
const HISTORY_PAGE = 20;
const BEST_ROWS = 14;
/** The big buttons at 0.9x and the small ones at 1x, with their labels (as the online lobby places its buttons). */
const BIG_SCALE = 0.9;
/**
 * The clans tab on a phone (a wide host showing less than a CSS px per stage px: the tab's 10-11 px text measured
 * 7-8 CSS px): its body text measures PHONE_TEXT_CSS CSS px (at most PHONE_TEXT_MAX px as the tab draws it), the
 * other texts in step with it; the tab's parts are laid out anew round the text (phoneLayout).
 */
const PHONE_TEXT_CSS = 10.8;
const PHONE_TEXT_MAX = 18;
/** The tab's column on a phone (the panel's paper: 229..612) and the room between its parts. */
const PHONE_COL = { x: 244, width: 350 };
const PHONE_GAP = 8;
/** menu_friends' list mask (a clip layer, shape 579: 251..568 x 201..459) and the slider's track (shape 869). */
const LIST_MASK_ID = 579;
const LIST_TRACK_ID = 869;
/** The small buttons' art (btn_small: 85 x 30) and the most one takes on a phone. */
const SMALL_W = 85;
const SMALL_SCALE_MAX = 1.35;

type HubView = 'clan' | 'requests';
type Selected = { kind: 'member' | 'request' | 'myRequest'; id: string } | null;

interface Card {
  clanId: number;
  info: ClanInfo | null;
  members: ClanMember[] | null;
  history: ClanEvent[];
  historyNext: string | null;
  /** A page of its history is on its way. */
  historyLoading?: boolean;
  failed: boolean;
  /** The card's status line (a refusal, the request sent). */
  message: string | null;
  messageError: boolean;
}

interface Edit {
  mode: 'create' | 'settings';
  emblem: number;
  open: boolean;
  error: string | null;
}

function codeOf(err: any): string {
  return err && typeof err.code === 'string' ? err.code : 'UNAVAILABLE';
}

function reasonOf(err: any): string | null {
  const r = err && err.details && typeof err.details === 'object' ? (err.details as any).reason : null;
  return typeof r === 'string' ? r : null;
}

/** The unnamed text field of `link` at (x, y) (a static text of the screen). */
function textAt(link: any, at: { x: number; y: number }): any | null {
  for (let k = 0; link && k < link.numChildren; k++) {
    const c = link.getChildAt(k);
    if (c instanceof TextField && Math.abs(c.x - at.x) < 4 && Math.abs(c.y - at.y) < 4) return c;
  }
  return null;
}

/** The unnamed child of `link` placed at (x, y) (a static shape of the screen). */
function childAt(link: any, at: { x: number; y: number }): any | null {
  for (let k = 0; link && k < link.numChildren; k++) {
    const c = link.getChildAt(k);
    if (!(c instanceof TextField) && Math.abs(c.x - at.x) < 0.5 && Math.abs(c.y - at.y) < 0.5) return c;
  }
  return null;
}

/**
 * Sets DISPLAY text (already translated; names as typed) with formatting: the whole text in `base`, the ranges of
 * `spans` in theirs.
 */
function rich(tf: any, text: string, base: { color?: number; bold?: boolean; italic?: boolean; size?: number } = {}, spans: { at?: [number, number]; bold?: boolean; italic?: boolean; color?: number }[] = []): void {
  if (base.size) {
    const d = tf.defaultTextFormat;
    d.size = base.size;
    tf.defaultTextFormat = d;
  }
  setRawText(tf, text);
  if (!text) return;
  const f = new TextFormat();
  f.bold = !!base.bold;
  f.italic = !!base.italic;
  f.color = base.color ?? 0;
  if (base.size) f.size = base.size;
  tf.setTextFormat(f, 0, text.length);
  for (const s of spans) {
    if (!s.at || s.at[1] <= s.at[0]) continue;
    const g = new TextFormat();
    if (s.bold !== undefined) g.bold = s.bold;
    if (s.italic !== undefined) g.italic = s.italic;
    if (s.color !== undefined) g.color = s.color;
    tf.setTextFormat(g, s.at[0], s.at[1]);
  }
}

/** A list being filled (ClanHub.list): its container, the next row's y and, on a phone, the rows' text, step and width. */
interface List {
  box: any;
  y: number;
  phone: { text: number; pitch: number; width: number } | null;
}

/** The list's mask of the clans tab (menu_friends: a clip layer) as the asset patches place it. */
const LIST_MASK = { x: 251, y: 201, width: 317, height: 258 };

/**
 * A row's step on a phone: a transparent box of it (0..`step` from the row's top, `width` wide: the row's bounds, and
 * the taps of the whole row where `button` is null) and the button's hit area the same rectangle.
 */
function phoneStep(r: any, button: any, x: number, width: number, step: number): void {
  const box = new Sprite();
  box.name = '$phone_step';
  box.graphics.beginFill(0, 0);
  box.graphics.drawRect(x, 0, width, step);
  box.graphics.endFill();
  if (button) {
    box.mouseEnabled = false;
    const p0 = button.globalToLocal(r.localToGlobal(new Point(x, 0)));
    const p1 = button.globalToLocal(r.localToGlobal(new Point(x + width, step)));
    const hit = new Sprite();
    hit.graphics.beginFill(0, 0);
    hit.graphics.drawRect(Math.min(p0.x, p1.x), Math.min(p0.y, p1.y), Math.abs(p1.x - p0.x), Math.abs(p1.y - p0.y));
    hit.graphics.endFill();
    button.hitTestState = hit;
  }
  r.addChildAt(box, 0);
}

function roleText(role: string | null | undefined): string {
  return role === 'leader' ? T.MSG_ROLE_LEADER : role === 'officer' ? T.MSG_ROLE_OFFICER : T.MSG_ROLE_MEMBER;
}

export class ClanHub {
  /** clan.mine (null until the first answer). */
  mine: ClanMineResult | null = null;
  /** The members of the player's clan (clan.members), null while loading. */
  members: ClanMember[] | null = null;
  history: ClanEvent[] = [];
  historyNext: string | null = null;
  historyLoaded = false;
  /** The last load of the members / the history failed: not loaded again by a render (only by an action, an event, entering the tab). */
  membersFailed = false;
  historyFailed = false;
  /** A clan event came while a popup was on top of the tab: the members and the history are loaded again on its next render. */
  hubStale = false;
  /** The clan's pending requests (leader and officers, the view 'requests'). */
  requests: ClanJoinRequest[] = [];
  requestsNext: string | null = null;
  requestsLoaded = false;
  /** The clan list of a player without a clan: by rating, or the search results of `query`. */
  clans: ClanSummary[] = [];
  clansNext: string | null = null;
  clansLoaded = false;
  clansFailed = false;
  query = '';
  view: HubView = 'clan';
  selected: Selected = null;
  /** The status line of the clans screen (a refusal, what happened). */
  message: string | null = null;
  messageIsError = false;
  card: Card | null = null;
  best: { clans: ClanSummary[]; loaded: boolean; failed: boolean } = { clans: [], loaded: false, failed: false };
  edit: Edit | null = null;
  /** The action a question popup confirms. */
  confirm: { action: 'leave' | 'kick' | 'transfer' | 'disband'; playerId?: string; name?: string } | null = null;
  /** A request of the clans screen is in flight (clicks wait for it). */
  busy = false;
  /** Diagnostics (tests). */
  readonly trace: string[] = [];

  private readonly tasks: (() => void)[] = [];
  private attached = false;
  private offs: (() => void)[] = [];
  /** Membership events the player's own action causes (no notice for them). */
  private readonly own = new Set<string>();
  /** Scroll positions (0..1) of the lists. */
  private readonly scroll: Record<string, number> = {};
  /** "Show more" was clicked: the list keeps its offset (px) once the new page makes it longer than `height`. */
  private readonly keep: Record<string, { offset: number; height: number }> = {};
  /** Generation of the hub's loads: an answer of an older generation (another clan, another view) is dropped. */
  private gen = 0;
  /** Loads in flight (a render that finds its data missing starts the load once). */
  private readonly loading = new Set<string>();
  /** The timeline-placed texts and shapes of a clans screen (their instance names cannot change). */
  private readonly statics = new WeakMap<object, { title: any; meetCaption: any; meetLook: any }>();
  /** The clans tab built for a phone: its body text (px), the list's geometry laid out last. */
  private readonly phones = new WeakMap<object, { text: number; geo: { top: number; height: number; sliderTop: number; sliderBottom: number } | null }>();

  constructor(readonly fwp: any) {}

  get services(): any {
    return this.fwp.services;
  }

  get playerId(): string {
    return String(this.fwp.user.player_id ?? '');
  }

  /** The backend offers the clans (test doubles without them: the tab says so). */
  get available(): boolean {
    const s = this.services;
    return !!s && typeof s.getMyClan === 'function' && typeof s.listClansPage === 'function' && typeof s.createClan === 'function';
  }

  /** The player's role in its clan (null: no clan). */
  get role(): ClanRole | null {
    return this.mine && this.mine.clan ? this.mine.role : null;
  }

  get staff(): boolean {
    return this.role === 'leader' || this.role === 'officer';
  }

  private log(line: string): void {
    this.trace.push(line);
    if (this.trace.length > 200) this.trace.splice(0, this.trace.length - 200);
  }

  private queue(fn: () => void): void {
    this.tasks.push(fn);
  }

  /** Subscribes to the server's clan events (once). */
  attach(): void {
    if (this.attached || !this.available || typeof this.services.on !== 'function') return;
    this.attached = true;
    const s = this.services;
    this.offs.push(
      s.on('clan.updated', (e: any) => this.queue(() => this.onUpdated(e.clan))),
      s.on('clan.membership', (e: any) => this.queue(() => this.onMembership(e))),
      s.on('clan.requestsChanged', (e: any) => this.queue(() => this.onRequestsChanged(e))),
    );
    // Missed events (a gap after a reconnect): the player's clan may have changed meanwhile.
    if (typeof s.onEventGap === 'function')
      this.offs.push(
        s.onEventGap(() =>
          this.queue(() => {
            this.hubStale = true;
            this.refreshMine(() => (this.isHub() ? this.loadHub() : undefined));
          }),
        ),
      );
  }

  dispose(): void {
    for (const off of this.offs) off();
    this.offs = [];
    this.attached = true;
  }

  /** Every frame of FWP._main (not while a blocking call runs): the server's events and the answers. */
  frame(): void {
    this.attach();
    for (let n = this.tasks.length; n > 0 && this.tasks.length > 0; n--) this.tasks.shift()!();
  }

  // ------------------------------------------------------------------------------------------------ screens

  private top(): { name: string; link: any } | null {
    const i = this.fwp._interf;
    return i && i.cur_screen && i.cur_screen[0] ? i.cur_screen[i.cur_screen[0]] : null;
  }

  private on(name: string): boolean {
    return this.top()?.name === name;
  }

  isHub(): boolean {
    return this.on('clan_hub');
  }

  isCard(): boolean {
    return this.on('clan_card');
  }

  isEdit(): boolean {
    return this.on('clan_edit');
  }

  isBest(): boolean {
    return this.on('clans');
  }

  isQuestion(): boolean {
    return this.on('question') && (QUESTIONS as readonly string[]).includes(this.fwp._interf.question_mode);
  }

  /** Renders the screen on top again when it is one of this module's. */
  private rerender(): void {
    if (this.isHub() || this.isCard() || this.isEdit() || this.isBest() || this.isQuestion()) this.fwp._interf.render();
  }

  /** A short message over the game (DISPLAY text: translated already, names as typed). */
  private notice(text: string): void {
    this.fwp.net_notice(text, 4000, true);
  }

  /** Closes the popups on top of a tab screen (instantly). */
  private closePopups(): void {
    const f = this.fwp;
    const i = f._interf;
    for (let guard = 0; i.cur_screen[0] > 0 && guard < 20; guard++) {
      const name = i.cur_screen[i.cur_screen[0]].name;
      if (name in i.no_anim_def) break;
      // A popup still sliding in or out (its cross clicked a moment ago) goes now: the slide must not
      // go on with the tab screen under it (a finished "hide" would remove the tab screen as well).
      if (f.animation) {
        f.animation = false;
        f.set_invisible_button(false);
        f.execute_after_animation = "";
      }
      i.remove_instant();
    }
  }

  // ------------------------------------------------------------------------------------------------ the player's clan

  /** clan.mine: the player's clan (user_clan), its brief under the player's uid (the tops, the tribute). */
  adoptMine(m: ClanMineResult): void {
    this.mine = m;
    const f = this.fwp;
    f.user_clan = m.clan ? m.clan.clanId : 0;
    if (f.user && f.user.user_id) f.note_clan(f.user.user_id, m.clan ? { clanId: m.clan.clanId, name: m.clan.name, emblem: m.clan.emblem } : null);
  }

  private refreshMine(then?: () => void): void {
    if (!this.available) return;
    this.services.getMyClan().then(
      (m: ClanMineResult) =>
        this.queue(() => {
          const before = this.mine?.clan?.clanId ?? null;
          const roleBefore = this.role;
          this.adoptMine(m);
          const changed = (m.clan?.clanId ?? null) !== before;
          if (changed) this.resetClanData();
          else if (!this.staff) {
            // Demoted: the requests are not the player's to decide any more.
            if (this.view === 'requests') this.view = 'clan';
            if (this.selected?.kind === 'request') this.selected = null;
            this.requests = [];
            this.requestsLoaded = false;
          }
          if (then) then();
          this.rerender();
          // The assault map and the tribute show the player's clan too.
          if ((changed || roleBefore !== this.role) && (this.on('assault') || this.on('tribute'))) this.fwp._interf.render();
        }),
      (err: any) => this.queue(() => this.failed(err, false)),
    );
  }

  /** The data of the player's clan is of another clan now (joined, left): forget it. */
  private resetClanData(): void {
    this.gen++;
    this.members = null;
    this.history = [];
    this.historyNext = null;
    this.historyLoaded = false;
    this.membersFailed = false;
    this.historyFailed = false;
    this.requests = [];
    this.requestsNext = null;
    this.requestsLoaded = false;
    this.view = 'clan';
    this.selected = null;
    delete this.scroll.clan_hub;
    delete this.keep.clan_hub;
  }

  /** What the hub shows is loaded (again): the members, history and requests of the clan, or the clan list. */
  private loadHub(): void {
    const c = this.mine?.clan;
    if (c) {
      this.loadMembers();
      this.loadHistory(true);
      if (this.staff && this.view === 'requests') this.loadRequests(true);
    } else this.loadClans(true);
  }

  private loadMembers(): void {
    const clanId = this.mine?.clan?.clanId;
    if (!clanId) return;
    const gen = this.gen;
    this.membersFailed = false;
    this.loading.add('members');
    this.services.getClanMembers(clanId).then(
      (items: ClanMember[]) =>
        this.queue(() => {
          this.loading.delete('members');
          if (gen !== this.gen) return;
          this.members = items;
          if (this.selected?.kind === 'member' && !items.some((m) => m.playerId === this.selected!.id)) this.selected = null;
          this.rerender();
        }),
      (err: any) =>
        this.queue(() => {
          this.loading.delete('members');
          if (gen !== this.gen) return;
          this.membersFailed = true;
          this.failed(err, false);
        }),
    );
  }

  private loadHistory(reset: boolean): void {
    const clanId = this.mine?.clan?.clanId;
    if (!clanId) return;
    const gen = this.gen;
    const cursor = reset ? undefined : (this.historyNext ?? undefined);
    this.historyFailed = false;
    const key = reset ? 'history' : 'history_more';
    this.loading.add(key);
    this.services.getClanHistoryPage(clanId, cursor ? { cursor, limit: HISTORY_PAGE } : { limit: HISTORY_PAGE }).then(
      (r: { items: ClanEvent[]; next: string | null }) =>
        this.queue(() => {
          this.loading.delete(key);
          if (gen !== this.gen) return;
          // A page after a list loaded again meanwhile (its cursor is not the list's any more) is dropped.
          if (!reset && (this.historyNext ?? undefined) !== cursor) return;
          this.history = reset ? r.items : [...this.history, ...r.items];
          this.historyNext = r.next;
          this.historyLoaded = true;
          this.rerender();
        }),
      (err: any) =>
        this.queue(() => {
          this.loading.delete(key);
          if (gen !== this.gen) return;
          this.historyFailed = true;
          this.failed(err, false);
        }),
    );
  }

  private loadRequests(reset: boolean): void {
    const clanId = this.mine?.clan?.clanId;
    if (!clanId || !this.staff) return;
    const gen = this.gen;
    const cursor = reset ? undefined : (this.requestsNext ?? undefined);
    const key = reset ? 'requests' : 'requests_more';
    this.loading.add(key);
    this.services.listClanRequests(clanId, cursor ? { cursor } : {}).then(
      (r: { items: ClanJoinRequest[]; next: string | null }) =>
        this.queue(() => {
          this.loading.delete(key);
          if (gen !== this.gen) return;
          // A page after a list loaded again meanwhile (its cursor is not the list's any more) is dropped.
          if (!reset && (this.requestsNext ?? undefined) !== cursor) return;
          this.requests = reset ? r.items : [...this.requests, ...r.items];
          this.requestsNext = r.next;
          this.requestsLoaded = true;
          if (this.selected?.kind === 'request' && !this.requests.some((q) => q.playerId === this.selected!.id)) this.selected = null;
          this.rerender();
        }),
      (err: any) =>
        this.queue(() => {
          this.loading.delete(key);
          if (gen === this.gen) this.failed(err, false);
        }),
    );
  }

  private loadClans(reset: boolean): void {
    if (!this.available) return;
    const gen = this.gen;
    const cursor = reset ? undefined : (this.clansNext ?? undefined);
    const opts: any = { limit: CLAN_PAGE };
    if (cursor) opts.cursor = cursor;
    if (this.query) opts.query = this.query;
    if (reset) {
      this.clansLoaded = false;
      this.clansFailed = false;
    }
    const key = reset ? 'clans' : 'clans_more';
    this.loading.add(key);
    this.services.listClansPage(opts).then(
      (r: { clans: ClanSummary[]; next: string | null }) =>
        this.queue(() => {
          this.loading.delete(key);
          if (gen !== this.gen) return;
          // A page after a list loaded again meanwhile (its cursor is not the list's any more) is dropped.
          if (!reset && (this.clansNext ?? undefined) !== cursor) return;
          this.clans = reset ? r.clans : [...this.clans, ...r.clans];
          this.clansNext = r.next;
          this.clansLoaded = true;
          this.rerender();
        }),
      (err: any) =>
        this.queue(() => {
          this.loading.delete(key);
          if (gen !== this.gen) return;
          this.clansLoaded = true;
          this.clansFailed = true;
          this.failed(err, false);
        }),
    );
  }

  // ------------------------------------------------------------------------------------------------ server events

  private onUpdated(clan: ClanInfo): void {
    if (!clan) return;
    if (this.mine && this.mine.clan && this.mine.clan.clanId === clan.clanId) {
      this.mine.clan = clan;
      this.adoptMine(this.mine);
      if (this.isHub()) {
        this.loadMembers();
        this.loadHistory(true);
      } else this.hubStale = true;
    }
    if (this.card && this.card.clanId === clan.clanId) this.card.info = clan;
    this.rerender();
  }

  private onMembership(e: { clanId: number | null; role: ClanRole | null; reason: string }): void {
    if (!e) return;
    this.log(`membership ${e.reason} ${e.clanId} ${e.role}`);
    const mineBefore = !this.own.delete(e.reason);
    this.refreshMine(() => {
      if (mineBefore) {
        const clan = this.mine?.clan?.name ?? '';
        if (e.reason === 'accepted') this.notice(T.fill(T.TPL_N_ACCEPTED, { clan }));
        else if (e.reason === 'kicked') this.notice(translate(T.MSG_N_KICKED));
        else if (e.reason === 'disbanded') this.notice(translate(T.MSG_N_DISBANDED));
        else if (e.reason === 'role') this.notice(translate(e.role === 'officer' ? T.MSG_N_OFFICER : T.MSG_N_MEMBER));
        else if (e.reason === 'leader') this.notice(translate(T.MSG_N_LEADER));
      }
      // The screens of the clan the player is no longer in (or of the new one) show what is now.
      if (this.isHub()) this.loadHub();
      else this.hubStale = true;
    });
  }

  private onRequestsChanged(e: { clanId: number; pending: number }): void {
    if (!e || !this.mine || !this.mine.clan || this.mine.clan.clanId !== e.clanId) return;
    const before = this.mine.pendingRequests;
    this.mine.pendingRequests = e.pending;
    if (e.pending > before) this.notice(translate(T.MSG_N_REQUESTS));
    if (this.view === 'requests') {
      if (this.isHub()) this.loadRequests(true);
      else this.hubStale = true;
    }
    this.rerender();
  }

  // ------------------------------------------------------------------------------------------------ requests

  /** A refused or failed request: its text in the status line (and a notice); `busy` ends. */
  private failed(err: any, loud = true): void {
    this.busy = false;
    if (sessionEnded(this.fwp, err)) return;
    const reason = reasonOf(err);
    let text = T.reasonText(reason);
    // The level the server asks for (its configuration, CLAN_MIN_LEVEL).
    if (reason === 'clan_level_too_low' && Number(err?.details?.minLevel) > 0) text = T.fill(T.TPL_CREATE_RULES, { level: Number(err.details.minLevel) });
    // The refusals about another player (not the player's own state), and the members / requests they show are old.
    if (reason === 'already_in_clan' && err?.details?.playerId) text = translate(T.MSG_PLAYER_IN_CLAN);
    if (reason === 'clan_rank_too_low' && err?.details?.need === 'leader') text = translate(T.MSG_LEADER_ONLY);
    if (loud && this.mine?.clan && (reason === 'already_in_clan' || reason === 'clan_rank_too_low' || reason === 'not_a_member' || reason === 'request_not_found')) {
      if (this.isHub() || this.isQuestion()) this.hubStale = true;
    }
    const retry = Number(err?.details?.retryAfterMs);
    if (reason && Number.isFinite(retry) && retry > 0) text = T.fill(T.TPL_RETRY_IN, { what: text, time: T.durationText(retry) });
    this.log(`request failed: ${codeOf(err)} ${reason ?? ''} ${String(err?.message ?? '')}`);
    if (loud || !this.message) {
      this.message = text;
      this.messageIsError = true;
    }
    // (the clan form shows its refusal in its own line: no toast over its buttons as well)
    const inForm = !!this.edit && this.isEdit();
    if (loud && !inForm) this.notice(text);
    if (inForm) this.edit!.error = text;
    if (this.card && this.isCard()) {
      this.card.message = text;
      this.card.messageError = true;
    }
    this.rerender();
  }

  /**
   * A mutation of the clans screen: `busy` while it runs; `ok` in the game loop. `marks`: the reasons of the clan.membership events it causes (the player's own
   * doing: no notice for them); forgotten again when the request fails.
   */
  private act<R>(label: string, call: () => Promise<R>, ok: (r: R) => void, marks: string[] = []): void {
    if (this.busy || !this.available) return;
    this.busy = true;
    for (const m of marks) this.own.add(m);
    this.message = null;
    this.log(label);
    call().then(
      (r: R) =>
        this.queue(() => {
          this.busy = false;
          ok(r);
          this.rerender();
        }),
      (err: any) =>
        this.queue(() => {
          for (const m of marks) this.own.delete(m);
          this.failed(err);
        }),
    );
  }

  private say(text: string): void {
    this.message = text;
    this.messageIsError = false;
  }

  // ------------------------------------------------------------------------------------------------ entering the screens

  /** new_interf init_instant of a screen of this module (and of the best clans / a clan question), before render. */
  enter(name: string, param: string): void {
    this.attach();
    if (name === 'clan_hub') {
      this.message = null;
      this.query = '';
      this.view = 'clan';
      this.selected = null;
      delete this.scroll.clan_hub;
      delete this.keep.clan_hub;
      this.hubStale = false;
      if (this.available) this.refreshMine(() => this.loadHub());
    } else if (name === 'clan_card') {
      this.openCardData(Number(param));
    } else if (name === 'clan_edit') {
      const c = this.mine?.clan;
      const mode = param === 'settings' && c ? 'settings' : 'create';
      const emblems = this.emblems();
      this.edit = { mode, emblem: mode === 'settings' && c ? c.emblem : emblems[0], open: mode === 'settings' && c ? c.joinPolicy === 'open' : false, error: null };
    } else if (name === 'clans') {
      this.best = { clans: [], loaded: false, failed: false };
      if (!this.available) {
        this.best.loaded = true;
        return;
      }
      this.services.listClansPage({ limit: BEST_ROWS }).then(
        (r: { clans: ClanSummary[] }) =>
          this.queue(() => {
            this.best = { clans: r.clans, loaded: true, failed: false };
            if (this.isBest()) this.fwp._interf.render();
          }),
        () =>
          this.queue(() => {
            this.best = { clans: [], loaded: true, failed: true };
            if (this.isBest()) this.fwp._interf.render();
          }),
      );
    }
  }

  private openCardData(clanId: number): void {
    const card: Card = { clanId, info: null, members: null, history: [], historyNext: null, failed: false, message: null, messageError: false };
    this.card = card;
    delete this.keep.clan_card;
    if (!this.available || !clanId) {
      card.failed = true;
      return;
    }
    const s = this.services;
    const mine = (fn: () => void) => this.queue(() => (this.card === card ? fn() : undefined));
    const fail = (err: any) =>
      mine(() => {
        card.failed = true;
        card.messageError = true;
        card.message = codeOf(err) === 'NOT_FOUND' ? translate(T.MSG_CLAN_NOT_FOUND) : translate(T.MSG_LOAD_FAILED);
        this.rerender();
      });
    s.getClan(clanId).then((info: ClanInfo) => mine(() => ((card.info = info), this.rerender())), fail);
    s.getClanMembers(clanId).then((items: ClanMember[]) => mine(() => ((card.members = items), this.rerender())), fail);
    this.loadCardHistory(card, true);
  }

  private loadCardHistory(card: Card, reset: boolean): void {
    const cursor = reset ? undefined : (card.historyNext ?? undefined);
    card.historyLoading = true;
    this.services.getClanHistoryPage(card.clanId, cursor ? { cursor, limit: HISTORY_PAGE } : { limit: HISTORY_PAGE }).then(
      (r: { items: ClanEvent[]; next: string | null }) =>
        this.queue(() => {
          card.historyLoading = false;
          if (this.card !== card) return;
          if (!reset && (card.historyNext ?? undefined) !== cursor) return;
          card.history = reset ? r.items : [...card.history, ...r.items];
          card.historyNext = r.next;
          this.rerender();
        }),
      () => this.queue(() => (card.historyLoading = false)),
    );
  }

  /** The emblems a clan may choose (limits.clans.emblems of the server, else the shared rules). */
  emblems(): number[] {
    const l = this.services?.serverInfo?.limits?.clans?.emblems;
    return Array.isArray(l) && l.length ? l.map(Number) : [...CLAN_EMBLEMS];
  }

  private minLevel(): number {
    const n = Number(this.services?.serverInfo?.limits?.clans?.minLevelToCreate);
    return Number.isFinite(n) && n > 0 ? n : 5;
  }

  // ------------------------------------------------------------------------------------------------ building the screens

  /** A button of the original (a linkage class) with its label, as the online lobby places its buttons. */
  private button(link: any, cls: string, name: string, text: string, x: number, y: number, scale = 1): any {
    const b = this.fwp.container_interf.get_object(cls);
    b.name = name;
    b.scaleX = b.scaleY = scale;
    link.addChild(b);
    const r = b.getBounds(link);
    b.x = x - r.x;
    b.y = y - r.y;
    const small = cls === 'btn_small';
    const caption = label('', { size: small ? 11 : 12, bold: true, color: LABEL_COLOR, align: 'center', width: r.width });
    caption.name = name + '_label';
    caption.x = x;
    link.addChild(caption);
    (b as any).$box = { y, height: r.height, width: r.width, size: small ? 11 : 12 };
    this.setLabel(link, name, text);
    return b;
  }

  /** The label of a button of `button` (DISPLAY text or a catalogue key), shrunk until it fits. */
  private setLabel(link: any, name: string, text: string): void {
    const b = link.getChildByName(name);
    const caption = link.getChildByName(name + '_label');
    if (!b || !caption) return;
    const box = (b as any).$box;
    const fmt = caption.defaultTextFormat;
    fmt.size = box.size;
    caption.defaultTextFormat = fmt;
    caption.text = text;
    for (let size = box.size - 1; caption.textWidth > box.width - 8 && size >= 9; size--) {
      fmt.size = size;
      caption.defaultTextFormat = fmt;
      caption.text = text;
    }
    // (a larger caption on a phone: its field as tall as its line)
    caption.height = Math.max(caption.height, Math.ceil(caption.textHeight) + 4);
    caption.y = Math.round(box.y + (box.height - caption.textHeight) / 2 - 2);
  }

  private show(link: any, name: string, on: boolean): void {
    const b = link.getChildByName(name);
    if (b) b.visible = on;
    const caption = link.getChildByName(name + '_label');
    if (caption) caption.visible = on;
  }

  /** A button and its label greyed out and deaf to taps (`on` false), or as the original has them. */
  private enable(link: any, name: string, on: boolean): void {
    const b = link.getChildByName(name);
    const caption = link.getChildByName(name + '_label');
    for (const o of [b, caption]) if (o) o.alpha = on ? 1 : 0.45;
    if (b) {
      b.mouseEnabled = on;
      if ('enabled' in b) b.enabled = on;
    }
  }

  private add<D extends DisplayObject>(link: any, o: D, name: string, x: number, y: number): D {
    o.name = name;
    o.x = x;
    o.y = y;
    link.addChild(o);
    return o;
  }

  private emblemClip(): any {
    const e = this.fwp.container_interf.get_object('clan_emblem');
    e.gotoAndStop(NO_EMBLEM_FRAME);
    return e;
  }

  /**
   * new_interf init_instant: the controls of a screen of this module, added before the screen's children get their
   * click and hover listeners (so they are its direct children like the SWF's own controls).
   */
  build(name: string, link: any): void {
    if (name === 'clan_hub') this.buildHub(link);
    else if (name === 'clan_card') this.buildCard(link);
    else if (name === 'clan_edit') this.buildEdit(link);
    else if (name === 'clans') this.buildBest(link);
    else if (name === 'question' && (QUESTIONS as readonly string[]).includes(this.fwp._interf.question_mode)) this.buildQuestion(link);
  }

  /**
   * The body text (px) of the clans tab on this stage's phone (a wide host showing less than a CSS px per stage px:
   * PHONE_TEXT_CSS CSS px), or 0: the tab as it is elsewhere (a desktop, a tablet).
   */
  private phoneText(): number {
    const st = this.fwp.stage;
    const css = st && st.$visibleArea ? Number(st.cssScale) : 0;
    if (!(css > 0) || css >= 0.99) return 0;
    return Math.min(PHONE_TEXT_MAX, Math.max(12, Math.ceil(PHONE_TEXT_CSS / css)));
  }

  private buildHub(link: any): void {
    // On a phone the texts are larger, in step with the body text (11 px elsewhere).
    const B = this.phoneText();
    if (B) this.phones.set(link, { text: B, geo: null });
    const sz = (n: number) => (B ? B + n - 11 : n);
    // The screen's own texts and shapes placed by its timeline keep their (instance) names: they are remembered here.
    const title = textAt(link, HUB.title.at);
    if (title) {
      title.x = HUB.title.x;
      title.width = HUB.title.width;
    }
    this.statics.set(link, { title, meetCaption: textAt(link, HUB.meetCaption), meetLook: childAt(link, HUB.meetShape) });
    this.add(link, this.emblemClip(), 'clan_head_emblem', HUB.emblem.x, HUB.emblem.y);
    this.add(link, label('', { size: sz(13), bold: true, width: HUB.name.width }), 'clan_head_name', HUB.name.x, HUB.name.y);
    this.add(link, label('', { size: sz(10), color: 0x555555, align: 'right', width: HUB.policy.width }), 'clan_head_policy', HUB.policy.x, HUB.policy.y);
    this.add(link, label('', { size: sz(11), color: INFO_COLOR, width: HUB.facts.width, lines: B ? 2 : 1 }), 'clan_head_facts', HUB.facts.x, HUB.facts.y);
    this.add(link, label('', { size: sz(10), color: 0x555555, width: HUB.desc.width, lines: 2 }), 'clan_head_desc', HUB.desc.x, HUB.desc.y);
    this.add(link, label('', { size: sz(11), align: 'center', width: HUB.intro.width, lines: B ? 6 : 4 }), 'clan_intro', HUB.intro.x, HUB.intro.y);
    this.add(link, label('', { size: sz(10), color: 0x555555, width: HUB.selected.width, lines: B ? 2 : 1 }), 'clan_selected', HUB.selected.x, HUB.selected.y);
    for (const b of ['clan_kick', 'clan_promote', 'clan_demote', 'clan_transfer', 'clan_settings', 'clan_accept', 'clan_decline', 'clan_cancel_request']) this.button(link, 'btn_small', b, '', HUB.f1Buttons.x[0], HUB.f1Buttons.y);
    this.button(link, 'btn_yellow', 'clan_create', T.MSG_CREATE_CLAN, HUB.create.x, HUB.create.y);
    this.add(link, label('', { size: sz(10), color: 0x555555, width: HUB.createNote.width, lines: B ? 5 : 3 }), 'clan_create_note', HUB.createNote.x, HUB.createNote.y);
    this.button(link, 'btn_yellow', 'clan_leave', T.MSG_LEAVE, HUB.leave.x, HUB.leave.y);
    this.add(link, label(T.MSG_SEARCH_LABEL, { size: sz(10), color: 0x555555, width: HUB.search.width }), 'clan_search_label', HUB.search.x, HUB.search.y);
    this.add(link, label('', { size: sz(11), width: HUB.status.width, lines: 2 }), 'clan_status', HUB.status.x, HUB.status.y);
    if (B) {
      // The buttons' captions in step with the text (the big ones' 12 px, the small ones' 11 px), set by setLabel.
      for (const n of ['clan_create', 'clan_leave']) (link.getChildByName(n) as any).$box.size = sz(12);
      for (const n of ['clan_kick', 'clan_promote', 'clan_demote', 'clan_transfer', 'clan_settings', 'clan_accept', 'clan_decline', 'clan_cancel_request']) (link.getChildByName(n) as any).$box.size = sz(11);
      for (const n of ['clan_create', 'clan_leave']) this.setLabel(link, n, n === 'clan_create' ? T.MSG_CREATE_CLAN : T.MSG_LEAVE);
    }
    if (link.id_to_meet) {
      link.id_to_meet.maxChars = 20;
      link.id_to_meet.text = '';
    }
  }

  private buildCard(link: any): void {
    this.add(link, this.emblemClip(), 'card_emblem', CARD.emblem.x, CARD.emblem.y);
    this.add(link, label('', { size: 13, bold: true, width: CARD.name.width }), 'card_name', CARD.name.x, CARD.name.y);
    this.add(link, label('', { size: 11, color: INFO_COLOR, width: CARD.facts.width, lines: 2 }), 'card_facts', CARD.facts.x, CARD.facts.y);
    this.add(link, label('', { size: 11, color: 0x555555, width: CARD.desc.width, lines: 3 }), 'card_desc', CARD.desc.x, CARD.desc.y);
    this.button(link, 'btn_green', 'card_action', '', CARD.action.x, CARD.action.y);
    this.add(link, label('', { size: 11, color: HINT_COLOR, align: 'center', width: CARD.status.width, lines: 2 }), 'card_status', CARD.status.x, CARD.status.y);
  }

  private buildEdit(link: any): void {
    // The popup of the member list without its list: the dialog's own fields.
    if (link.friends_container) link.friends_container.visible = false;
    if (link.slider) link.slider.visible = false;
    for (let k = 0; k < link.numChildren; k++) {
      const c = link.getChildAt(k);
      if (Math.abs(c.x + 53) < 0.5) c.visible = false; // the slider's track (shape 578 at x -53)
    }
    const e = this.edit!;
    this.add(link, label(e.mode === 'create' ? T.MSG_NEW_CLAN : T.MSG_CLAN_SETTINGS, { size: 12, bold: true, align: 'center', width: EDIT.title.width }), 'edit_title', EDIT.title.x, EDIT.title.y);
    const nameLabel = this.add(link, label('', { size: 11 }), 'edit_name_label', EDIT.nameLabel.x, EDIT.nameLabel.y);
    rich(nameLabel, T.fill(T.TPL_NAME_LABEL, { min: CLAN_NAME_MIN, max: CLAN_NAME_MAX }));
    this.add(link, inputField('edit_name', { width: EDIT.name.width, maxChars: CLAN_NAME_MAX + 4 }), 'edit_name', EDIT.name.x, EDIT.name.y);
    this.add(link, label(T.MSG_EMBLEM_LABEL, { size: 11 }), 'edit_emblem_label', EDIT.emblemLabel.x, EDIT.emblemLabel.y);
    this.emblems().forEach((n, k) => {
      const clip = this.add(link, this.emblemClip(), 'emblem_' + n, EDIT.emblems.x + (k % EDIT.emblems.perRow) * EDIT.emblems.dx, EDIT.emblems.y + Math.floor(k / EDIT.emblems.perRow) * EDIT.emblems.dy);
      clip.gotoAndStop(emblemFrame(n));
      clip.buttonMode = true;
      clip.mouseChildren = false;
    });
    const frame = this.add(link, new Shape(), 'emblem_chosen', 0, 0);
    frame.graphics.lineStyle(2, CHOSEN_COLOR, 1);
    frame.graphics.drawRect(-3, -3, 38, 38);
    const check = this.add(link, this.fwp.container_interf.get_object('check_box'), 'edit_open', EDIT.open.x, EDIT.open.y);
    check.gotoAndStop(1);
    this.add(link, label(T.MSG_OPEN_JOIN, { size: 11, width: EDIT.openLabel.width }), 'edit_open_label', EDIT.openLabel.x, EDIT.openLabel.y + 1);
    const descLabel = this.add(link, label('', { size: 11, width: EDIT.descLabel.width }), 'edit_desc_label', EDIT.descLabel.x, EDIT.descLabel.y);
    rich(descLabel, T.fill(T.TPL_DESCRIPTION_LABEL, { max: CLAN_DESCRIPTION_MAX }));
    const desc = this.add(link, inputField('edit_desc', { width: EDIT.desc.width, maxChars: CLAN_DESCRIPTION_MAX }), 'edit_desc', EDIT.desc.x, EDIT.desc.y);
    desc.multiline = true;
    desc.wordWrap = true;
    desc.height = EDIT.desc.height;
    const fmt = desc.defaultTextFormat;
    fmt.size = 12;
    desc.defaultTextFormat = fmt;
    this.add(link, label('', { size: 10, color: 0x555555, width: EDIT.rules.width, lines: 4 }), 'edit_rules', EDIT.rules.x, EDIT.rules.y);
    this.add(link, label('', { size: 11, color: ERROR_COLOR, width: EDIT.error.width, lines: 2 }), 'edit_error', EDIT.error.x, EDIT.error.y);
    if (e.mode === 'create') this.button(link, 'btn_green', 'edit_save', T.MSG_CREATE_CLAN, EDIT.save.x, EDIT.save.y);
    else {
      this.button(link, 'btn_green', 'edit_save', T.MSG_SAVE, EDIT.saveSettings.x, EDIT.saveSettings.y, BIG_SCALE);
      this.button(link, 'btn_yellow', 'edit_disband', T.MSG_DISBAND, EDIT.disband.x, EDIT.disband.y, BIG_SCALE);
      const c = this.mine?.clan;
      if (c) {
        setRawText(link.getChildByName('edit_name'), c.name);
        setRawText(desc, c.description);
      }
    }
  }

  private buildBest(link: any): void {
    this.add(link, label('', { size: 12, color: NOTE_COLOR, align: 'center', width: 280 }), 'clans_note', 263, 100);
  }

  private buildQuestion(link: any): void {
    if (this.fwp._interf.question_mode === 'clan_disband') this.add(link, inputField('clan_confirm_name', { width: QUESTION_INPUT.width, maxChars: CLAN_NAME_MAX + 4 }), 'clan_confirm_name', QUESTION_INPUT.x, QUESTION_INPUT.y);
  }

  // ------------------------------------------------------------------------------------------------ rendering

  /** new_interf.render of a screen of this module. */
  render(name: string, link: any): void {
    if (name === 'clan_hub') this.renderHub(link);
    else if (name === 'clan_card') this.renderCard(link);
    else if (name === 'clan_edit') this.renderEdit(link);
    else if (name === 'clans') this.renderBest(link);
    else if (name === 'question') this.renderQuestion(link);
  }

  /** The header of a clan: its emblem, name, facts (the numbers in bold) and description. */
  private header(link: any, names: { emblem: string; name: string; facts: string; desc: string; policy?: string }, c: ClanSummary & { description?: string }): void {
    setEmblem(link.getChildByName(names.emblem), c.emblem);
    const name = link.getChildByName(names.name);
    setRawText(name, c.name);
    const policy = translate(c.joinPolicy === 'open' ? T.MSG_POLICY_OPEN : T.MSG_POLICY_REQUEST);
    // The clans tab shows the join policy right of the name (its facts line is one line), the card in its facts.
    if (names.policy) {
      const p = link.getChildByName(names.policy);
      setRawText(p, policy);
      // The name ends before the policy (a long name gets a smaller font).
      name.width = Math.max(60, p.x + p.width - 2 - p.textWidth - 8 - name.x);
    }
    fitText(name, 1);
    const values = { rating: c.rating, members: c.memberCount, max: c.maxMembers, zones: c.zoneCount, policy };
    const facts = T.compose(names.policy ? T.TPL_FACTS_SHORT : T.TPL_FACTS, values);
    rich(link.getChildByName(names.facts), facts.text, { color: INFO_COLOR }, ['rating', 'members', 'zones'].map((k) => ({ at: facts.at[k], bold: true })));
    const desc = link.getChildByName(names.desc);
    rich(desc, c.description ?? '', { color: 0x555555, italic: true });
    fitText(desc, names.policy ? 2 : 3);
  }

  /**
   * The list container: rows added one under another; `y` of the next row. On a phone (the clans tab built for one)
   * `phone`: the rows' text (px), the step of a row one taps (ui/wide-lists.ts listPitch) and the rows' width.
   */
  private list(link: any): List {
    const box = link.friends_container;
    while (box.numChildren > 0) box.removeChildAt(0);
    const ph = this.phones.get(link);
    return { box, y: 0, phone: ph ? { text: ph.text, pitch: listPitch(this.fwp.stage), width: LIST_MASK.width - 6 } : null };
  }

  private rowAt(l: List, name: string, text: string, opts: { color?: number; bold?: boolean; italic?: boolean; spans?: any[]; clickable?: boolean; gapBefore?: number; x?: number } = {}): any {
    if (l.phone) return this.phoneRow(l, l.phone, name, text, opts);
    if (opts.gapBefore) l.y += opts.gapBefore;
    const r = this.fwp.container_interf.get_object('choose_task');
    r.name = name;
    r.x = opts.x ?? 0;
    r.y = l.y;
    l.box.addChild(r);
    const tf = r.task_name;
    rich(tf, text, { color: opts.color ?? 0, bold: opts.bold, italic: opts.italic }, opts.spans ?? []);
    let h = ROW;
    if (tf.textWidth > tf.width - 4) {
      // As many lines as a long text takes (a history event with long names).
      tf.wordWrap = true;
      tf.multiline = true;
      h = ROW + (tf.numLines - 1) * ROW_LINE;
      tf.height = h - 2;
      if (r.choose_task_button) r.choose_task_button.scaleY *= h / ROW;
    }
    if (opts.clickable === false) r.mouseEnabled = r.mouseChildren = false;
    l.y += h;
    return r;
  }

  /**
   * A row on a phone: its text (the list's size, wrapped in the rows' width) in the middle of its step, the step a
   * finger's target (listPitch) for a row one taps, the text's height with a margin for a heading, a note, an event;
   * the row's button takes the taps of all of it, and a transparent box of the step makes the list's height its
   * rows' (the slider's scrolling).
   */
  private phoneRow(l: List, ph: NonNullable<List['phone']>, name: string, text: string, opts: { color?: number; bold?: boolean; italic?: boolean; spans?: any[]; clickable?: boolean; gapBefore?: number; x?: number }): any {
    if (opts.gapBefore) l.y += Math.round(opts.gapBefore * 1.5);
    const r = this.fwp.container_interf.get_object('choose_task');
    r.name = name;
    r.x = opts.x ?? 0;
    r.y = l.y;
    l.box.addChild(r);
    const tf = r.task_name;
    tf.wordWrap = true;
    tf.multiline = true;
    tf.width = ph.width - r.x;
    rich(tf, text, { color: opts.color ?? 0, bold: opts.bold, italic: opts.italic, size: ph.text }, opts.spans ?? []);
    const h = Math.ceil(tf.textHeight) + 4;
    tf.height = h;
    const clickable = opts.clickable !== false;
    const step = clickable ? Math.max(ph.pitch, h + 8) : h + 6;
    tf.y = Math.round((step - h) / 2);
    phoneStep(r, r.choose_task_button, 0, ph.width - r.x, step);
    if (!clickable) r.mouseEnabled = r.mouseChildren = false;
    l.y += step;
    return r;
  }

  private heading(l: List, name: string, text: string, gapBefore = 0): void {
    this.rowAt(l, name, text, { color: HEADING_COLOR, bold: true, clickable: false, gapBefore });
  }

  private note(l: List, name: string, text: string, clickable = false): void {
    this.rowAt(l, name, translate(text), { color: NOTE_COLOR, italic: true, clickable });
  }

  /** The slider of a list: shown when the rows are taller than the visible part, the list scrolled to its position. */
  private layoutList(key: string, link: any, geo: { top: number; height: number; sliderTop: number; sliderBottom: number }): void {
    const box = link.friends_container;
    const slider = link.slider;
    const h = box.height;
    if (h <= geo.height + 1) {
      if (slider) slider.visible = false;
      box.y = geo.top;
      this.scroll[key] = 0;
      return;
    }
    const kept = this.keep[key];
    if (kept && h > kept.height) {
      // The rows loaded by "show more" start where the "show more" row was: the view stays where it was.
      this.scroll[key] = kept.offset / (h - geo.height);
      delete this.keep[key];
    }
    const p = Math.max(0, Math.min(1, this.scroll[key] ?? 0));
    if (slider) {
      slider.visible = true;
      slider.y = geo.sliderTop + p * (geo.sliderBottom - geo.sliderTop);
    }
    box.y = geo.top - p * (h - geo.height);
  }

  /** Before a "show more": the list's offset now, kept when the next page arrives (layoutList). */
  private keepScroll(key: string, geo: { top: number }): void {
    const box = this.top()?.link?.friends_container;
    if (box) this.keep[key] = { offset: geo.top - box.y, height: box.height };
  }

  /** new_interf.slider_proceed: the slider of a list of this module was dragged to `p` (0..1). */
  slide(name: string, link: any, p: number): void {
    const geo = name === 'clan_hub' ? (this.phones.get(link)?.geo ?? HUB.list) : name === 'clan_card' ? CARD.list : null;
    if (!geo || !link.friends_container) return;
    this.scroll[name] = p;
    const box = link.friends_container;
    box.y = geo.top - p * Math.max(0, box.height - geo.height);
  }

  /** The range of the slider of a screen of this module (new_interf.init_slider), or null. */
  sliderRange(name: string): { top: number; bottom: number } | null {
    const hub = name === 'clan_hub' ? this.top()?.link : null;
    const geo = name === 'clan_hub' ? ((hub && this.phones.get(hub)?.geo) ?? HUB.list) : name === 'clan_card' ? CARD.list : null;
    return geo ? { top: geo.sliderTop, bottom: geo.sliderBottom } : null;
  }

  private renderHub(link: any): void {
    const c = this.mine?.clan ?? null;
    const title = this.hubStatic(link).title;
    const ph = this.phones.get(link);
    if (title) title.htmlText = '<p align="center"><font face="Arial" size="' + (ph ? ph.text + 1 : 12) + '" color="#000000"><b>' + (c ? T.MSG_CLAN : T.MSG_CLANS) + '</b></font></p>';
    for (const n of ['clan_head_emblem', 'clan_head_name', 'clan_head_policy', 'clan_head_facts', 'clan_head_desc']) link.getChildByName(n).visible = !!c;
    link.getChildByName('clan_intro').visible = !c;
    const l = this.list(link);
    // What the screen shows and nobody has loaded yet (the player's clan changed while a popup was on top of the
    // screen): loaded now, once.
    if (this.available && this.mine) {
      if (c && this.hubStale) {
        this.hubStale = false;
        if (!this.loading.has('members')) this.loadMembers();
        if (!this.loading.has('history')) this.loadHistory(true);
        if (this.staff && this.view === 'requests') this.loadRequests(true);
      }
      if (c && !this.members && !this.membersFailed && !this.loading.has('members')) this.loadMembers();
      if (c && !this.historyLoaded && !this.historyFailed && !this.loading.has('history')) this.loadHistory(true);
      if (!c && !this.clansLoaded && !this.loading.has('clans')) this.loadClans(true);
    }
    if (!this.available) {
      link.getChildByName('clan_intro').text = T.MSG_CLANS_UNAVAILABLE;
      this.hubActions(link, null);
      this.layoutList('clan_hub', link, ph ? this.phoneLayout(link, null) : HUB.list);
      return;
    }
    if (c) {
      this.header(link, { emblem: 'clan_head_emblem', name: 'clan_head_name', facts: 'clan_head_facts', desc: 'clan_head_desc', policy: 'clan_head_policy' }, c);
      if (this.view === 'requests' && this.staff) this.requestRows(l);
      else this.clanRows(l, c);
    } else {
      link.getChildByName('clan_intro').text = T.MSG_NO_CLAN_INTRO;
      if (!this.mine) this.note(l, 'hub_info', T.MSG_LOADING);
      else this.noClanRows(l);
    }
    this.hubActions(link, c);
    this.layoutList('clan_hub', link, ph ? this.phoneLayout(link, c) : HUB.list);
  }

  /**
   * The clans tab on a phone, laid out round its larger text (renderHub, after its texts and actions): the title, the
   * header (the clan's emblem, name, join policy, facts and description; or the introduction) from the top, the actions
   * from the bottom (the status line, the green button with the leave button or the search field, above them the
   * chosen player's line and its buttons, or "Create a clan" and why not yet), the list between them (its mask, the
   * slider's track and range with it). Returns the list's geometry.
   */
  private phoneLayout(link: any, c: ClanInfo | null): { top: number; height: number; sliderTop: number; sliderBottom: number } {
    const ph = this.phones.get(link)!;
    const B = ph.text;
    const col = PHONE_COL;
    const right = col.x + col.width;
    const fit = (tf: any) => {
      tf.height = tf.text ? Math.ceil(tf.textHeight) + 4 : 0;
      return tf.height;
    };
    const top = (o: any, y: number) => {
      o.y += y - o.getBounds(link).y;
    };
    const st = this.hubStatic(link);
    let y = 112;
    if (st.title) {
      st.title.y = y;
      y += fit(st.title) + 2;
    }
    // The header from the top.
    let headerBottom = y;
    if (c && link.getChildByName('clan_head_name').visible) {
      const emblem = link.getChildByName('clan_head_emblem');
      const eb = emblem.getBounds(emblem);
      emblem.scaleX = emblem.scaleY = 40 / Math.max(1, Math.max(eb.width, eb.height));
      emblem.x = col.x + 8 - eb.x * emblem.scaleX;
      emblem.y = y + 2 - eb.y * emblem.scaleY;
      const x = col.x + 8 + 40 + 8;
      const name = link.getChildByName('clan_head_name');
      const policy = link.getChildByName('clan_head_policy');
      const facts = link.getChildByName('clan_head_facts');
      const desc = link.getChildByName('clan_head_desc');
      policy.x = right - policy.width;
      name.x = x;
      name.width = Math.max(60, policy.x + policy.width - 2 - policy.textWidth - 8 - name.x);
      fitText(name, 1);
      name.y = y;
      fit(name);
      fit(policy);
      policy.y = name.y + Math.round((name.height - policy.height) / 2);
      facts.x = x;
      facts.width = right - x;
      facts.y = name.y + name.height;
      fit(facts);
      desc.x = col.x + 8;
      desc.width = col.width - 8;
      desc.y = Math.max(facts.y + facts.height, y + 44) + 2;
      headerBottom = desc.text ? desc.y + fit(desc) : Math.max(facts.y + facts.height, y + 44);
    } else {
      const intro = link.getChildByName('clan_intro');
      intro.y = y;
      headerBottom = y + fit(intro);
    }
    // The actions from the bottom: the status line, the row of the big buttons (the green one of the original with
    // the leave button, or the search field), the search's label, the chosen one's line and buttons or "Create".
    const status = link.getChildByName('clan_status');
    status.height = Math.ceil(B * 1.15) + 4;
    status.y = 575 - status.height;
    const rowA = status.y - 2 - 40;
    for (const o of [link.meet_id, st.meetLook]) if (o) top(o, rowA);
    if (st.meetCaption) {
      fit(st.meetCaption);
      st.meetCaption.y = rowA + Math.round((40 - st.meetCaption.height) / 2);
    }
    this.placeButton(link, 'clan_leave', col.x + 7, rowA);
    let bottom = rowA;
    const none = !c && !!this.mine;
    if (none) {
      const field = link.id_to_meet;
      const meetX = link.meet_id ? link.meet_id.getBounds(link).x : col.x + 185;
      if (field) {
        field.x = col.x + 7;
        field.width = meetX - 12 - field.x;
        field.height = 32;
        field.y = rowA + 4;
      }
      const lbl = link.getChildByName('clan_search_label');
      lbl.x = col.x + 7;
      lbl.y = rowA - 2 - fit(lbl);
      bottom = lbl.y;
    }
    const f1 = ['clan_kick', 'clan_promote', 'clan_demote', 'clan_transfer', 'clan_settings', 'clan_accept', 'clan_decline', 'clan_cancel_request'].filter((n) => link.getChildByName(n).visible);
    if (f1.length) {
      const gap = 6;
      const room = (col.width - 14 - gap * (f1.length - 1)) / f1.length;
      const k = Math.min(SMALL_SCALE_MAX, room / SMALL_W);
      // (as wide as the longest caption at the text's size needs, where the row has room: not a shrunk caption)
      let need = 0;
      for (const n of f1) {
        const caption = link.getChildByName(n + '_label');
        const f = caption.defaultTextFormat;
        f.size = (link.getChildByName(n) as any).$box.size;
        caption.defaultTextFormat = f;
        caption.text = caption.text;
        need = Math.max(need, caption.textWidth + 16);
      }
      const w = Math.round(Math.max(SMALL_W * k, Math.min(room, need)));
      const h = Math.round(30 * k);
      bottom -= PHONE_GAP + h;
      f1.forEach((n, i) => {
        const b = link.getChildByName(n);
        b.scaleY = k;
        b.scaleX = w / SMALL_W;
        this.placeButton(link, n, col.x + 7 + i * (w + gap), bottom);
      });
    }
    const sel = link.getChildByName('clan_selected');
    if (sel.visible && sel.text) {
      sel.x = col.x + 7;
      sel.width = col.width - 14;
      fit(sel);
      bottom -= 4 + sel.height;
      sel.y = bottom;
    }
    if (link.getChildByName('clan_create').visible) {
      const note = link.getChildByName('clan_create_note');
      note.x = col.x + 7 + 145 + 8;
      note.width = right - note.x;
      const rowH = Math.max(40, fit(note));
      bottom -= PHONE_GAP + rowH;
      this.placeButton(link, 'clan_create', col.x + 7, bottom + Math.round((rowH - 40) / 2));
      note.y = bottom + Math.round((rowH - note.height) / 2);
    }
    // The list between them: its mask and the slider's track with it.
    const listTop = headerBottom + PHONE_GAP;
    const height = Math.max(60, bottom - PHONE_GAP - listTop);
    for (let i = 0; i < link.numChildren; i++) {
      const o = link.getChildAt(i);
      if (o.$charId !== LIST_MASK_ID && o.$charId !== LIST_TRACK_ID) continue;
      const own = o.getBounds(o);
      if (!(own.height > 0)) continue;
      o.scaleY = (height + (o.$charId === LIST_TRACK_ID ? 4 : 0)) / own.height;
      top(o, listTop);
    }
    ph.geo = { top: listTop, height, sliderTop: listTop + 15, sliderBottom: listTop + height - 11 };
    return ph.geo;
  }

  /** Puts a button of `button` (and its caption) with its art's top left at (x, y), its box as it now measures. */
  private placeButton(link: any, name: string, x: number, y: number): void {
    const b = link.getChildByName(name);
    const caption = link.getChildByName(name + '_label');
    if (!b) return;
    const r = b.getBounds(link);
    b.x += x - r.x;
    b.y += y - r.y;
    const box = (b as any).$box;
    if (box) {
      box.y = y;
      box.width = r.width;
      box.height = r.height;
    }
    if (caption) {
      caption.x = x;
      caption.width = r.width;
      this.setLabel(link, name, caption.text);
    }
  }

  /** The overview of the player's clan: players, territories, history. */
  private clanRows(l: List, c: ClanInfo): void {
    const members = this.members;
    this.heading(l, 'sec_players', T.fill(T.TPL_SECTION_PLAYERS, { n: c.memberCount, max: c.maxMembers }));
    if (!members) this.note(l, 'players_info', T.MSG_LOADING);
    else
      members.forEach((m, k) => {
        const name = m.playerId === this.playerId ? T.fill(T.TPL_YOU, { name: playerName(m.displayName) }) : playerName(m.displayName);
        const t = T.compose(T.TPL_MEMBER, { name, role: translate(roleText(m.role)), level: m.level, captures: m.captures });
        const chosen = this.selected?.kind === 'member' && this.selected.id === m.playerId;
        this.rowAt(l, 'm_' + k, t.text, { color: chosen ? CHOSEN_COLOR : 0, spans: [{ at: t.at.name, bold: true }] });
      });
    this.zoneRows(l, c.zones, 'z_');
    this.historyRows(l, this.history, this.historyLoaded, this.historyNext, 'h_');
  }

  private zoneRows(l: List, zones: ClanZone[], prefix: string): void {
    this.heading(l, prefix + 'sec', T.fill(T.TPL_SECTION_ZONES, { n: zones.length }), SECTION_GAP);
    if (!zones.length) this.note(l, prefix + 'none', T.MSG_NO_ZONES);
    for (const z of [...zones].sort((a, b) => a.zone - b.zone)) {
      const t = T.compose(T.TPL_ZONE, { zone: z.zone, owner: z.ownerName, stage: z.stage, gold: this.fwp.x3String(z.tributePerHour) });
      this.rowAt(l, prefix + z.zone, t.text, { spans: [{ at: t.at.owner, bold: true }] });
    }
  }

  private historyRows(l: List, events: ClanEvent[], loaded: boolean, next: string | null, prefix: string): void {
    this.heading(l, prefix + 'sec', translate(T.MSG_SECTION_HISTORY), SECTION_GAP);
    if (!loaded && !events.length) this.note(l, prefix + 'info', T.MSG_LOADING);
    else if (!events.length) this.note(l, prefix + 'info', T.MSG_NO_HISTORY);
    const now = Date.now();
    events.forEach((e, k) => {
      const date = T.dateText(e.at, now);
      const line = T.eventText(e);
      const text = date + ' ' + line.text;
      const shift = date.length + 1;
      const spans: any[] = [{ at: [0, date.length], color: DATE_COLOR }];
      for (const key of ['actor', 'target', 'by']) if (line.at[key]) spans.push({ at: [line.at[key][0] + shift, line.at[key][1] + shift], bold: true });
      this.rowAt(l, prefix + k, text, { spans, clickable: false });
    });
    if (next) this.note(l, prefix + 'more', T.MSG_MORE, true);
  }

  /** The clan's pending requests (leader and officers). */
  private requestRows(l: List): void {
    this.heading(l, 'sec_requests', T.fill(T.TPL_SECTION_REQUESTS, { n: this.mine?.pendingRequests ?? this.requests.length }));
    if (!this.requestsLoaded) this.note(l, 'requests_info', T.MSG_LOADING);
    else if (!this.requests.length) this.note(l, 'requests_info', T.MSG_NO_REQUESTS);
    const now = Date.now();
    this.requests.forEach((q, k) => {
      const t = T.compose(T.TPL_REQUEST, { name: playerName(q.displayName), level: q.level, when: T.dateText(q.at, now) });
      const chosen = this.selected?.kind === 'request' && this.selected.id === q.playerId;
      this.rowAt(l, 'r_' + k, t.text, { color: chosen ? CHOSEN_COLOR : 0, spans: [{ at: t.at.name, bold: true }] });
    });
    if (this.requestsNext) this.note(l, 'r_more', T.MSG_MORE, true);
  }

  /** A player without a clan: its requests, then the clans by rating or the search results. */
  private noClanRows(l: List): void {
    const mine = this.mine!;
    if (mine.requests.length) {
      this.heading(l, 'sec_my_requests', translate(T.MSG_SECTION_MY_REQUESTS));
      for (const q of mine.requests) {
        const t = T.compose(T.TPL_MY_REQUEST, { clan: q.clanName });
        const chosen = this.selected?.kind === 'myRequest' && this.selected.id === String(q.clanId);
        this.rowAt(l, 'q_' + q.clanId, t.text, { color: chosen ? CHOSEN_COLOR : 0, spans: [{ at: t.at.clan, bold: true }] });
      }
      l.y += SECTION_GAP;
    }
    this.heading(l, 'sec_clans', this.query ? T.fill(T.TPL_SECTION_SEARCH, { query: this.query }) : translate(T.MSG_SECTION_BY_RATING));
    l.y += 2;
    if (!this.clansLoaded) this.note(l, 'clans_info', T.MSG_LOADING);
    else if (this.clansFailed && !this.clans.length) this.note(l, 'clans_info', T.MSG_LOAD_FAILED);
    else if (!this.clans.length) this.note(l, 'clans_info', this.query ? T.MSG_NOTHING_FOUND : T.MSG_NO_CLANS);
    this.clans.forEach((c, k) => {
      const r = this.fwp.container_interf.get_object('clip_clan');
      r.name = 'c_' + k;
      r.x = 6;
      r.y = l.y + 6;
      l.box.addChild(r);
      if (l.phone) {
        l.y += this.phoneClanRow(r, c, l.y, l.phone);
        return;
      }
      this.clanRow(r, c, 225);
      r.rate.x = 262;
      l.y += CLAN_ROW;
    });
    if (this.clansNext) this.note(l, 'c_more', T.MSG_MORE_CLANS, true);
  }

  /** A clip_clan row: the emblem, the name (in bold) with the members and the join policy, the rating. */
  private clanRow(r: any, c: ClanSummary, nameWidth: number): void {
    setEmblem(r.clan_icons, c.emblem);
    r.clan_icons.buttonMode = true;
    const t = T.compose(T.TPL_CLAN_ROW, { name: c.name, members: c.memberCount, max: c.maxMembers, policy: translate(c.joinPolicy === 'open' ? T.MSG_POLICY_OPEN_SHORT : T.MSG_POLICY_REQUEST_SHORT) });
    const tf = r.clan_name;
    tf.width = nameWidth;
    const fmt = tf.defaultTextFormat;
    fmt.size = 11;
    fmt.align = 'center';
    tf.defaultTextFormat = fmt;
    rich(tf, t.text, { color: DATE_COLOR }, [{ at: t.at.name, bold: true, color: 0 }]);
    // A long name: the name alone (the facts are on the card).
    if (tf.textWidth > nameWidth - 4) rich(tf, c.name, { bold: true });
    tf.selectable = false;
    r.rate.text = this.fwp.x3String(c.rating);
    r.rate.selectable = false;
  }

  /**
   * A clip_clan row on a phone: the emblem larger, the name and the facts in the list's text (wrapped, up to two lines)
   * between it and the rating, all in the middle of a step a finger's target; a transparent box takes the taps of the
   * whole step. Returns the step.
   */
  private phoneClanRow(r: any, c: ClanSummary, y: number, ph: NonNullable<List['phone']>): number {
    r.x = 0;
    r.y = y;
    const emblem = 40;
    const rateW = Math.round(ph.text * 3.6);
    const nameX = emblem + 8;
    this.clanRow(r, c, ph.width - nameX - rateW - 6);
    const tf = r.clan_name;
    tf.x = nameX;
    tf.wordWrap = true;
    tf.multiline = true;
    const t = T.compose(T.TPL_CLAN_ROW, { name: c.name, members: c.memberCount, max: c.maxMembers, policy: translate(c.joinPolicy === 'open' ? T.MSG_POLICY_OPEN_SHORT : T.MSG_POLICY_REQUEST_SHORT) });
    const fmt = tf.defaultTextFormat;
    fmt.align = 'left';
    tf.defaultTextFormat = fmt;
    rich(tf, t.text, { color: DATE_COLOR, size: ph.text }, [{ at: t.at.name, bold: true, color: 0 }]);
    if (tf.numLines > 2) rich(tf, c.name, { bold: true, size: ph.text });
    tf.height = Math.ceil(tf.textHeight) + 4;
    const rate = r.rate;
    const rf = rate.defaultTextFormat;
    rf.size = ph.text;
    rf.align = 'right';
    rate.defaultTextFormat = rf;
    rate.text = this.fwp.x3String(c.rating);
    rate.width = rateW;
    rate.height = Math.ceil(rate.textHeight) + 4;
    rate.x = ph.width - rateW;
    const step = Math.max(ph.pitch, tf.height + 8, emblem + 8);
    tf.y = Math.round((step - tf.height) / 2);
    rate.y = Math.round((step - rate.height) / 2);
    const icon = r.clan_icons;
    const b = icon.getBounds(icon);
    const k = emblem / Math.max(1, Math.max(b.width, b.height));
    icon.scaleX = icon.scaleY = k;
    icon.x = -b.x * k;
    icon.y = Math.round((step - emblem) / 2) - b.y * k;
    phoneStep(r, null, 0, ph.width, step);
    return step;
  }

  /** The two rows of actions under the list. */
  private hubActions(link: any, c: ClanInfo | null): void {
    const inClan = !!c && this.available;
    const none = !c && this.available && !!this.mine;
    const x = HUB.f1Buttons.x;
    const f1: string[] = [];
    let selectedText = '';
    if (inClan) {
      const sel = this.selected;
      if (sel && sel.kind === 'member') {
        const m = this.members?.find((p) => p.playerId === sel.id);
        if (m) {
          selectedText = T.fill(T.TPL_SELECTED, { name: playerName(m.displayName) });
          if (m.playerId === this.playerId) selectedText += ' ' + translate(T.MSG_THIS_IS_YOU);
          else {
            if (this.role === 'leader' || (this.role === 'officer' && m.role === 'member')) f1.push('clan_kick');
            if (this.role === 'leader' && m.role === 'member') f1.push('clan_promote');
            if (this.role === 'leader' && m.role === 'officer') f1.push('clan_demote');
            if (this.role === 'leader') f1.push('clan_transfer');
          }
        }
      } else if (sel && sel.kind === 'request' && this.staff) {
        const q = this.requests.find((p) => p.playerId === sel.id);
        if (q) {
          selectedText = T.fill(T.TPL_SELECTED, { name: playerName(q.displayName) });
          f1.push('clan_accept', 'clan_decline');
        }
      } else if (this.staff && this.view === 'requests') selectedText = translate(T.MSG_REQUESTS_HINT);
      else if (this.staff) {
        f1.push('clan_settings');
        selectedText = translate(T.MSG_STAFF_HINT);
      } else selectedText = translate(T.MSG_MEMBER_HINT);
    } else if (none && this.selected?.kind === 'myRequest') {
      const q = this.mine!.requests.find((p) => String(p.clanId) === this.selected!.id);
      if (q) {
        selectedText = T.fill(T.TPL_SELECTED, { name: q.clanName });
        f1.push('clan_cancel_request');
      }
    }
    const labels: Record<string, string> = {
      clan_kick: T.MSG_KICK,
      clan_promote: T.MSG_PROMOTE,
      clan_demote: T.MSG_DEMOTE,
      clan_transfer: T.MSG_TRANSFER,
      clan_settings: T.MSG_SETTINGS,
      clan_accept: T.MSG_ACCEPT,
      clan_decline: T.MSG_DECLINE,
      clan_cancel_request: T.MSG_CANCEL_REQUEST,
    };
    for (const name of Object.keys(labels)) {
      const k = f1.indexOf(name);
      this.show(link, name, k >= 0);
      if (k < 0) continue;
      const b = link.getChildByName(name);
      const caption = link.getChildByName(name + '_label');
      const bx = b.getBounds(link).x;
      b.x += x[k] - bx;
      caption.x = x[k];
      this.setLabel(link, name, labels[name]);
    }
    const sel = link.getChildByName('clan_selected');
    sel.visible = !!selectedText && (inClan || none);
    setRawText(sel, selectedText);
    // No clan: create (or why not yet: the button greyed out beside the reason, not a form refused after it is filled
    // in), search by name.
    this.show(link, 'clan_create', none && !this.selected);
    this.enable(link, 'clan_create', !none || !!this.mine?.canCreate?.ok);
    const note = link.getChildByName('clan_create_note');
    note.visible = none && !this.selected;
    if (none) {
      const can = this.mine!.canCreate;
      let text = T.fill(T.TPL_CREATE_RULES, { level: this.minLevel() });
      if (!can.ok) {
        text = can.reason === 'clan_level_too_low' ? T.fill(T.TPL_CREATE_RULES, { level: this.minLevel() }) : T.reasonText(can.reason);
        if (can.at) text = T.fill(T.TPL_RETRY_IN, { what: T.reasonText(can.reason), time: T.durationText(Number(can.at) - this.serverNow()) });
      }
      setRawText(note, text);
      note.textColor = can.ok ? 0x555555 : ERROR_COLOR;
    }
    link.getChildByName('clan_search_label').visible = none;
    if (link.id_to_meet) link.id_to_meet.visible = none;
    // In a clan: leave; leader and officers: the requests (the green button of the original).
    this.show(link, 'clan_leave', inClan && this.view === 'clan');
    const green = inClan ? this.staff : none;
    const st = this.hubStatic(link);
    for (const o of [link.meet_id, st.meetCaption, st.meetLook]) if (o) o.visible = green;
    const caption = st.meetCaption;
    if (caption && green) {
      const text = inClan ? (this.view === 'requests' ? translate(T.MSG_BACK_TO_CLAN) : T.fill(T.TPL_REQUESTS_BUTTON, { n: this.mine?.pendingRequests ?? 0 })) : translate(T.MSG_FIND);
      caption.htmlText = '<p align="center"><font face="Arial" size="12" color="#333333"><b></b></font></p>';
      const ph = this.phones.get(link);
      if (ph) {
        // (the caption's size on a phone: the text set next takes the field's default format)
        const d = caption.defaultTextFormat;
        d.size = ph.text + 1;
        d.bold = true;
        caption.defaultTextFormat = d;
      }
      setRawText(caption, text);
    }
    const status = link.getChildByName('clan_status');
    setRawText(status, this.message ?? '');
    fitText(status, 1);
    status.textColor = this.messageIsError ? ERROR_COLOR : 0x006600;
  }

  /** The timeline-placed texts and shapes of the clans screen this module rewrites (buildHub). */
  hubStatic(link: any): { title: any; meetCaption: any; meetLook: any } {
    return this.statics.get(link) ?? { title: null, meetCaption: null, meetLook: null };
  }

  /** The server's time now (ms): the client clock (cooldowns shown roughly). */
  private serverNow(): number {
    return Date.now();
  }

  private renderCard(link: any): void {
    const card = this.card;
    const l = this.list(link);
    const info = card?.info ?? null;
    for (const n of ['card_emblem', 'card_name', 'card_facts', 'card_desc']) link.getChildByName(n).visible = !!info;
    if (info) this.header(link, { emblem: 'card_emblem', name: 'card_name', facts: 'card_facts', desc: 'card_desc' }, info);
    if (!card || (card.failed && !info)) this.note(l, 'card_info', card?.message ?? T.MSG_LOAD_FAILED);
    else if (!info) this.note(l, 'card_info', T.MSG_LOADING);
    else {
      this.heading(l, 'sec_players', T.fill(T.TPL_SECTION_PLAYERS, { n: info.memberCount, max: info.maxMembers }));
      if (!card.members) this.note(l, 'players_info', T.MSG_LOADING);
      else
        card.members.forEach((m, k) => {
          const t = T.compose(T.TPL_MEMBER, { name: playerName(m.displayName), role: translate(roleText(m.role)), level: m.level, captures: m.captures });
          this.rowAt(l, 'cm_' + k, t.text, { spans: [{ at: t.at.name, bold: true }], clickable: false });
        });
      this.zoneRows(l, info.zones, 'cz_');
      this.historyRows(l, card.history, true, card.historyNext, 'ch_');
    }
    this.layoutList('clan_card', link, CARD.list);
    // The button: join / ask / withdraw the request; nothing for the player's own clan or while in another one.
    const mine = this.mine;
    let action: string | null = null;
    let status = '';
    if (info && mine) {
      if (mine.clan && mine.clan.clanId === info.clanId) status = translate(T.MSG_YOUR_CLAN);
      else if (mine.clan) status = T.fill(T.TPL_IN_OTHER_CLAN, { clan: mine.clan.name });
      else if (mine.requests.some((q) => q.clanId === info.clanId)) action = T.MSG_WITHDRAW_REQUEST;
      else action = info.joinPolicy === 'open' ? T.MSG_JOIN : T.MSG_SEND_REQUEST;
    }
    if (card?.message && info) status = card.message;
    this.show(link, 'card_action', !!action);
    if (action) this.setLabel(link, 'card_action', action);
    const st = link.getChildByName('card_status');
    setRawText(st, status);
    fitText(st, 1);
    st.textColor = card?.message && card.messageError ? ERROR_COLOR : HINT_COLOR;
  }

  private renderEdit(link: any): void {
    const e = this.edit;
    if (!e) return;
    const officer = e.mode === 'settings' && this.role === 'officer';
    for (const n of ['edit_name_label', 'edit_name', 'edit_emblem_label', 'emblem_chosen', 'edit_open', 'edit_open_label']) {
      const o = link.getChildByName(n);
      if (o) o.visible = !officer;
    }
    this.show(link, 'edit_disband', e.mode === 'settings' && this.role === 'leader');
    const emblems = this.emblems();
    for (const n of emblems) {
      const o = link.getChildByName('emblem_' + n);
      if (o) o.visible = !officer;
    }
    const chosen = link.getChildByName('emblem_' + e.emblem);
    const frame = link.getChildByName('emblem_chosen');
    if (chosen && frame) {
      frame.x = chosen.x;
      frame.y = chosen.y;
    }
    link.getChildByName('edit_open').gotoAndStop(e.open ? 2 : 1);
    const rules = link.getChildByName('edit_rules');
    rules.text = e.mode === 'create' ? T.MSG_CREATE_RULES_LONG : officer ? T.MSG_OFFICER_EDIT : T.MSG_RENAME_RULE;
    setRawText(link.getChildByName('edit_error'), e.error ?? '');
  }

  private renderBest(link: any): void {
    const b = this.best;
    for (let k = 1; k <= BEST_ROWS; k++) {
      const row = link['clan' + k];
      if (!row) continue;
      const c = b.clans[k - 1];
      row.visible = !!c;
      if (!c) continue;
      setEmblem(row.clan_icons, c.emblem);
      row.clan_icons.buttonMode = true;
      setRawText(row.clan_name, c.name);
      fitText(row.clan_name, 1);
      row.clan_name.selectable = false;
      row.rate.text = this.fwp.x3String(c.rating);
      row.rate.selectable = false;
    }
    const note = link.getChildByName('clans_note');
    if (note) {
      note.text = !this.available ? T.MSG_CLANS_UNAVAILABLE : !b.loaded ? T.MSG_LOADING : b.failed ? T.MSG_LOAD_FAILED : b.clans.length ? '' : T.MSG_NO_CLANS;
      note.visible = !!note.text;
    }
  }

  private renderQuestion(link: any): void {
    const mode = this.fwp._interf.question_mode;
    const q = this.confirm;
    const clan = this.mine?.clan?.name ?? '';
    link.proceed.visible = true;
    if (mode === 'clan_leave') {
      link.header.text = T.MSG_Q_LEAVE;
      setRawText(link.main, T.fill((this.mine?.clan?.memberCount ?? 0) <= 1 ? T.TPL_Q_LEAVE_LAST : T.TPL_Q_LEAVE_TEXT, { clan }));
    } else if (mode === 'clan_kick') {
      link.header.text = T.MSG_Q_KICK;
      setRawText(link.main, T.fill(T.TPL_Q_KICK_TEXT, { name: q?.name ?? '' }));
    } else if (mode === 'clan_transfer') {
      link.header.text = T.MSG_Q_TRANSFER;
      setRawText(link.main, T.fill(T.TPL_Q_TRANSFER_TEXT, { name: q?.name ?? '' }));
    } else if (mode === 'clan_disband') {
      link.header.text = T.MSG_Q_DISBAND;
      link.main.text = T.MSG_Q_DISBAND_TEXT;
    }
    if (this.message && this.messageIsError && mode === 'clan_disband') setRawText(link.main, translate(T.MSG_Q_DISBAND_TEXT) + '\n' + this.message);
  }

  // ------------------------------------------------------------------------------------------------ clicks

  /** The name of the list row the target is in (rows are named by what they are). */
  private rowOf(target: any): string {
    for (let o = target; o; o = o.parent) {
      const n = String(o.name ?? '');
      if (/^(m|r|q|c|z|cz|h|ch)_\w+$/.test(n) || n === 'more') return n;
      if (n === 'friends_container') break;
    }
    return '';
  }

  /** new_interf.click: the controls of this module's screens (and the assault map's clan line). True = handled. */
  click(e: any): boolean {
    const target = e && e.target;
    const name = String(target?.name ?? '');
    const i = this.fwp._interf;
    if (this.isHub()) return this.clickHub(name, target);
    if (this.isCard()) return this.clickCard(name, target);
    if (this.isEdit()) return this.clickEdit(name, target);
    if (this.isQuestion()) {
      if (name === 'proceed') {
        this.confirmed();
        return true;
      }
      return false;
    }
    if (this.isBest()) {
      for (let o = target; o; o = o.parent) {
        const m = /^clan(\d+)$/.exec(String(o.name ?? ''));
        if (m) {
          const c = this.best.clans[Number(m[1]) - 1];
          if (c) i.init('clan_card', String(c.clanId));
          return true;
        }
      }
      return false;
    }
    if (this.on('assault') && (name === 'my_clan_name' || name === 'my_clan_icon' || target?.parent?.name === 'my_clan_icon')) {
      i.click_proceed('init_clan_hub');
      return true;
    }
    return false;
  }

  private clickHub(name: string, target: any): boolean {
    const i = this.fwp._interf;
    const link = this.top()!.link;
    const c = this.mine?.clan ?? null;
    if (name === 'choose_task_button' || name === 'clan_name' || name === 'rate' || this.rowOf(target).startsWith('c_')) {
      const row = this.rowOf(target);
      if (!row) return name === 'choose_task_button';
      // A page already on its way is not asked for again (a double click would add it twice).
      if (row.endsWith('_more')) this.keepScroll('clan_hub', this.phones.get(link)?.geo ?? HUB.list);
      if (row === 'h_more') {
        if (!this.loading.has('history_more')) this.loadHistory(false);
      } else if (row === 'r_more') {
        if (!this.loading.has('requests_more')) this.loadRequests(false);
      } else if (row === 'c_more') {
        if (!this.loading.has('clans_more')) this.loadClans(false);
      }
      else if (row.startsWith('m_')) {
        const m = this.members?.[Number(row.slice(2))];
        if (m) this.select({ kind: 'member', id: m.playerId });
      } else if (row.startsWith('r_')) {
        const q = this.requests[Number(row.slice(2))];
        if (q) this.select({ kind: 'request', id: q.playerId });
      } else if (row.startsWith('q_')) this.select({ kind: 'myRequest', id: row.slice(2) });
      else if (row.startsWith('z_')) this.openZone(Number(row.slice(2)));
      else if (row.startsWith('c_')) {
        const cl = this.clans[Number(row.slice(2))];
        if (cl) i.init('clan_card', String(cl.clanId));
      }
      return true;
    }
    if (this.busy && name.startsWith('clan_')) return true;
    const sel = this.selected;
    const member = sel?.kind === 'member' ? this.members?.find((m) => m.playerId === sel.id) : undefined;
    switch (name) {
      case 'meet_id':
        if (c) {
          this.view = this.view === 'requests' ? 'clan' : 'requests';
          this.selected = null;
          delete this.scroll.clan_hub;
          delete this.keep.clan_hub;
          if (this.view === 'requests') {
            this.requestsLoaded = false;
            this.loadRequests(true);
          }
          i.render();
        } else {
          const q = String(link.id_to_meet?.text ?? '').trim();
          this.query = q.length >= 2 ? q.slice(0, 20) : '';
          this.selected = null;
          delete this.scroll.clan_hub;
          delete this.keep.clan_hub;
          this.gen++;
          this.loadClans(true);
          i.render();
        }
        return true;
      case 'id_to_meet':
        return true;
      case 'clan_create':
        // (the button is greyed out then: the note beside it says why)
        if (this.mine && !this.mine.canCreate?.ok) return true;
        this.edit = null;
        i.init('clan_edit', 'create');
        return true;
      case 'clan_settings':
        i.init('clan_edit', 'settings');
        return true;
      case 'clan_leave':
        if (c && this.role === 'leader' && c.memberCount > 1) {
          this.message = T.reasonText('leader_must_transfer');
          this.messageIsError = true;
          i.render();
        } else if (c) this.ask('clan_leave', { action: 'leave' });
        return true;
      case 'clan_kick':
        if (member) this.ask('clan_kick', { action: 'kick', playerId: member.playerId, name: playerName(member.displayName) });
        return true;
      case 'clan_transfer':
        if (member) this.ask('clan_transfer', { action: 'transfer', playerId: member.playerId, name: playerName(member.displayName) });
        return true;
      case 'clan_promote':
      case 'clan_demote':
        if (member && c) {
          const role = name === 'clan_promote' ? 'officer' : 'member';
          this.act(`setRole ${member.playerId} ${role}`, () => this.services.setClanRole(c.clanId, member.playerId, role), () => {
            this.loadMembers();
            this.loadHistory(true);
          });
        }
        return true;
      case 'clan_accept':
      case 'clan_decline':
        if (sel?.kind === 'request' && c) {
          const accept = name === 'clan_accept';
          const playerId = sel.id;
          this.act(`decide ${playerId} ${accept}`, () => this.services.decideClanRequest(c.clanId, playerId, accept), () => {
            this.selected = null;
            this.requests = this.requests.filter((q) => q.playerId !== playerId);
            this.loadRequests(true);
            this.loadMembers();
            this.loadHistory(true);
            this.refreshMine();
          });
        }
        return true;
      case 'clan_cancel_request':
        if (sel?.kind === 'myRequest') {
          const clanId = Number(sel.id);
          this.act(`cancelRequest ${clanId}`, () => this.services.cancelClanRequest(clanId), () => {
            this.selected = null;
            this.refreshMine();
          });
        }
        return true;
    }
    return false;
  }

  private select(s: Selected): void {
    const same = this.selected && s && this.selected.kind === s.kind && this.selected.id === s.id;
    this.selected = same ? null : s;
    this.message = null;
    this.fwp._interf.render();
  }

  /** A territory of a list: the assault map with the zone chosen. */
  private openZone(zone: number): void {
    const i = this.fwp._interf;
    if (!(zone >= 1)) return;
    this.closePopups();
    i.click_proceed('init_assault');
    i.cur_element = zone;
  }

  /** A question popup of this module. */
  private ask(mode: (typeof QUESTIONS)[number], confirm: NonNullable<ClanHub['confirm']>): void {
    this.confirm = confirm;
    this.message = null;
    this.fwp._interf.init('question', mode);
  }

  /** The question's "proceed". */
  private confirmed(): void {
    const q = this.confirm;
    const c = this.mine?.clan;
    const i = this.fwp._interf;
    if (!q || this.busy) return;
    // Back to the clans screen (the popups above it close at once), with what happened in its status line.
    const back = () => {
      this.closePopups();
      if (this.isHub()) i.render();
    };
    if (q.action === 'leave') {
      // The last member's leaving disbands the clan (its event says so).
      const marks = (c?.memberCount ?? 0) <= 1 ? ['left', 'disbanded'] : ['left'];
      this.act('leave', () => this.services.leaveClan(), () => {
        this.say(translate(T.MSG_N_LEFT));
        this.notice(translate(T.MSG_N_LEFT));
        back();
        this.refreshMine(() => this.loadHub());
      }, marks);
    } else if (q.action === 'kick' && c && q.playerId) {
      const playerId = q.playerId;
      this.act(`kick ${playerId}`, () => this.services.kickClanMember(c.clanId, playerId), () => {
        this.selected = null;
        back();
        this.loadMembers();
        this.loadHistory(true);
        this.refreshMine();
      });
    } else if (q.action === 'transfer' && c && q.playerId) {
      const playerId = q.playerId;
      this.act(`transfer ${playerId}`, () => this.services.transferClan(c.clanId, playerId), () => {
        this.selected = null;
        back();
        this.refreshMine(() => this.loadHub());
      }, ['role']);
    } else if (q.action === 'disband' && c) {
      const typed = String(i.cur_screen[i.cur_screen[0]].link.getChildByName('clan_confirm_name')?.text ?? '').trim();
      if (!typed) return;
      this.act('disband', () => this.services.disbandClan(c.clanId, typed), () => {
        this.say(translate(T.MSG_N_DISBANDED));
        this.notice(translate(T.MSG_N_DISBANDED));
        back();
        this.refreshMine(() => this.loadHub());
      }, ['disbanded']);
    }
  }

  private clickCard(name: string, target: any): boolean {
    const card = this.card;
    const row = this.rowOf(target);
    if (name === 'choose_task_button') {
      if (row === 'ch_more' && card && !card.historyLoading) {
        this.keepScroll('clan_card', CARD.list);
        this.loadCardHistory(card, false);
      }
      else if (row.startsWith('cz_')) this.openZone(Number(row.slice(3)));
      return true;
    }
    if (name !== 'card_action' || !card || !card.info || this.busy) return name === 'card_action';
    const info = card.info;
    const pending = this.mine?.requests.some((q) => q.clanId === info.clanId);
    card.message = null;
    if (pending) {
      this.act(`cancelRequest ${info.clanId}`, () => this.services.cancelClanRequest(info.clanId), () => this.refreshMine());
    } else if (info.joinPolicy === 'open') {
      this.act(`join ${info.clanId}`, () => this.services.joinClan(info.clanId), () => {
        this.notice(T.fill(T.TPL_N_JOINED, { clan: info.name }));
        this.closePopups();
        this.refreshMine(() => (this.isHub() ? this.loadHub() : undefined));
      }, ['joined']);
    } else {
      this.act(`request ${info.clanId}`, () => this.services.requestClan(info.clanId), () => {
        card.message = translate(T.MSG_REQUEST_SENT);
        card.messageError = false;
        this.refreshMine();
      });
    }
    return true;
  }

  private clickEdit(name: string, target: any): boolean {
    const e = this.edit;
    if (!e) return false;
    const link = this.top()!.link;
    const m = /^emblem_(\d+)$/.exec(name);
    if (m) {
      e.emblem = Number(m[1]);
      this.fwp._interf.render();
      return true;
    }
    if (name === 'edit_open' || target?.parent?.name === 'edit_open') {
      e.open = !e.open;
      this.fwp._interf.render();
      return true;
    }
    if (name === 'edit_disband') {
      if (this.role === 'leader') this.ask('clan_disband', { action: 'disband' });
      return true;
    }
    if (name !== 'edit_save') return name === 'edit_name' || name === 'edit_desc';
    if (this.busy) return true;
    const rawName = String(link.getChildByName('edit_name')?.text ?? '');
    const rawDesc = String(link.getChildByName('edit_desc')?.text ?? '');
    const officer = e.mode === 'settings' && this.role === 'officer';
    const n = checkClanName(rawName);
    const d = checkClanDescription(rawDesc);
    e.error = null;
    if (!officer && !n.ok) e.error = T.reasonText(n.reason);
    else if (!d.ok) e.error = T.reasonText(d.reason);
    if (e.error) {
      this.fwp._interf.render();
      return true;
    }
    const description = d.ok ? d.description : '';
    if (e.mode === 'create' && n.ok) {
      this.act('create', () => this.services.createClan({ name: n.name, emblem: e.emblem, joinPolicy: e.open ? 'open' : 'request', description }), (info: ClanInfo) => {
        this.notice(T.fill(T.TPL_N_CREATED, { clan: info.name }));
        this.closePopups();
        this.refreshMine(() => (this.isHub() ? this.loadHub() : undefined));
      }, ['created']);
      return true;
    }
    const c = this.mine?.clan;
    if (!c) return true;
    const patch: any = { clanId: c.clanId };
    if (description !== c.description) patch.description = description;
    if (!officer && n.ok) {
      if (n.name !== c.name) patch.name = n.name;
      if (e.emblem !== c.emblem) patch.emblem = e.emblem;
      const policy = e.open ? 'open' : 'request';
      if (policy !== c.joinPolicy) patch.joinPolicy = policy;
    }
    if (Object.keys(patch).length === 1) {
      this.closePopups();
      return true;
    }
    this.act('update', () => this.services.updateClan(patch), (info: ClanInfo) => {
      if (this.mine) {
        this.mine.clan = info;
        this.adoptMine(this.mine);
      }
      this.say(translate(T.MSG_SAVED));
      this.closePopups();
      if (this.isHub()) {
        this.loadHistory(true);
        this.fwp._interf.render();
      }
    });
    return true;
  }

  /** The inputs of the screen named `name` (tests, tools): the texts a player typed. */
  field(name: string): any {
    return this.top()?.link?.getChildByName(name) ?? null;
  }
}
